import { StrokedText } from "@/components/ui/text/stroked-text";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Pokemon, PokemonState } from "prisma-layer";
import { InventoryPokemonsOutput } from "types/web-app";
import { useEffect, useMemo, useRef, useState } from "react";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { isXsScreen } from "@/utils/screen-size-check";
import { PokemonType } from "prisma-layer";
import cn from "classnames";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { getPokeData } from "game/data/pokemon/evolution";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";

type InventoryPokemon = InventoryPokemonsOutput["player_pokemons"][0];

export const PokeMiniItem = ({
  pokemon,
  id,
  level,
  selected,
  isInTeam,
  onClick,
  onLongPress,
  breedCount,
  boxIcon,
  boxColor,
}: {
  pokemon: InventoryPokemon;
  id: string;
  level: number;
  selected: boolean;
  isInTeam?: boolean;
  onClick: () => void;
  onLongPress?: () => void;
  talents?: PokemonType[];
  breedCount: number;
  boxIcon?: string | undefined;
  boxColor?: string | undefined;
}) => {
  const [isPressing, setIsPressing] = useState(false);

  // Hatch time constants
  const HATCH_TIME_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

  // Calculate hatch progress for eggs
  const getHatchProgress = () => {
    if (pokemon.state !== "EGG" || !pokemon.created_at)
      return { ratio: 0, remainingTime: "", isReady: false };

    const createdAt = new Date(pokemon.created_at).getTime();
    const now = Date.now();
    const elapsed = now - createdAt;

    if (elapsed >= HATCH_TIME_MS) {
      return { ratio: 1, remainingTime: "", isReady: true };
    }

    const ratio = elapsed / HATCH_TIME_MS;
    const remainingMs = HATCH_TIME_MS - elapsed;

    // Format remaining time
    const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor(
      (remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
    );

    let remainingTime = "";
    if (days > 0) {
      remainingTime = `${days}d ${hours}h`;
    } else if (hours > 0) {
      remainingTime = `${hours}h`;
    } else {
      remainingTime = "<1h";
    }

    return { ratio, remainingTime, isReady: false };
  };
  const timerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // cancel context menu on long-press (mobile)
  useEffect(() => {
    return () => clearTimer();
  }, []);

  const startPress = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onLongPress) return; // still allow normal click path
    setIsPressing(true);
    longPressFiredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };

    timerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      setIsPressing(false);
      onLongPress?.();
    }, 500);
  };

  const endPress = () => {
    setIsPressing(false);
    clearTimer();
  };

  const cancelIfMovedTooMuch = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPressing || !startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    const tolerance = 10; // px
    if (dx > tolerance || dy > tolerance) {
      // treat as cancel
      endPress();
    }
  };

  const handleClick = () => {
    // If long-press fired, swallow click
    if (longPressFiredRef.current) return;
    onClick?.();
  };

  const pokeData =
    useMemo(() => {
      return getPokeData(pokemon.base_pokemon_name);
    }, [pokemon.base_pokemon_name]) || null;

  return (
    <div
      className={cn(
        "flex group flex-col cursor-pointer hover:bg-pk-purple-1/60 rounded-sm bg-pk-purple-1 items-center justify-center relative pt-2 h-24",
        {
          "bg-pk-purple-4 hover:bg-pk-purple-4/60": selected,
          "opacity-50 grayscale cursor-not-allowed": isInTeam,
          "border-3 border-pk-orange-4": pokemon.is_new,
        },
      )}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      onPointerMove={cancelIfMovedTooMuch}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      {isInTeam && (
        <div className="absolute top-1 right-1 z-20">
          <div className="bg-pk-orange-4 text-white text-[8px] font-bold px-1 rounded-sm shadow-cartoon-sm">
            TEAM
          </div>
        </div>
      )}

      {/* Breed Count Badge */}
      {pokemon.state === "ADULT" && breedCount > 0 && (
        <div className="absolute top-1 right-1 z-1 flex items-center gap-0">
          <img src="/assets/icons/egg.png" alt="Egg" className="w-4 h-4" />
          <div className=" text-white text-[10px] font-bold px-1 rounded-sm  flex items-center -ml-1">
            {breedCount}
          </div>
        </div>
      )}
      {pokemon.is_new && (
        <div className="absolute top-0 left-0 z-20">
          <div className="bg-pk-orange-4 text-white text-xs font-bold px-1 rounded-br-sm shadow-cartoon-sm animate-pulse">
            NEW
          </div>
        </div>
      )}

      {/* Type Icons */}
      {pokeData && (
        <div className="absolute top-1 left-1 z-20 flex flex-col gap-1">
          <img
            src={`/assets/pokemon-types/${pokeData.types[0].toLowerCase()}.svg`}
            alt={pokeData.types[0]}
            className="drop-shadow-sm relative z-10"
            style={{
              width: 16,
              height: 16,
              opacity: 0.5,
            }}
          />

          {pokeData.types[1] && (
            <img
              src={`/assets/pokemon-types/${pokeData.types[1].toLowerCase()}.svg`}
              alt={pokeData.types[1]}
              className="drop-shadow-sm relative z-10"
              style={{
                width: 16,
                height: 16,
                opacity: 0.5,
              }}
            />
          )}
        </div>
      )}

      {boxIcon && (
        <div
          className="absolute opacity-60 top-0 right-0 z-20 p-0.5 rounded-bl-md border-b-2 border-l-2 border-white shadow-cartoon-sm"
          style={{ backgroundColor: boxColor || "#333" }}
        >
          <img
            src={`/assets/icons/boxes/${boxIcon}.svg`}
            className="w-3 h-3 fill-white stroke-white drop-shadow-md"
            alt="Box Icon"
          />
        </div>
      )}

      {pokemon.state === `ADULT` && (
        <div className="relative w-full h-full">
          <div className="absolute top-4/5 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <PokemonSprite
              pokemon={pokemon}
              style={{ scale: isXsScreen() ? 1.4 : 2 }}
              ignoreScale={true}
              showShadow
            />
          </div>
        </div>
      )}

      {pokemon.state === `EGG` && (
        <div className="relative w-full flex justify-center">
          <img
            src={`/assets/eggs/${pokemon.base_pokemon_name}.png`}
            alt={`${pokemon.base_pokemon_name} Egg`}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/assets/misc/poke-egg.png";
            }}
            className="w-8 h-auto my-auto relative z-10"
            style={{
              animation: "eggWiggle 1s ease-in-out infinite",
              transformOrigin: "center bottom",
            }}
          />
          {pokemon.is_shiny && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
              <AnimatedVFX
                id={`shiny-egg-${pokemon.id}`}
                spriteUrl="/assets/spells/vfx/self_sparkles_006__50.png"
                position={{ x: 0, y: 0 }}
                style={{ transform: "scale(1.5)" }}
                loops={-1}
                speed={0.5}
              />
            </div>
          )}
        </div>
      )}

      {pokemon.state === `ADULT` && (
        <div className="leading-4 text-sm text-pk-blue font-semibold mt-auto mb-1 z-50">
          Lv. {level}
        </div>
      )}

      {pokemon.state === `EGG` &&
        (() => {
          const { ratio, remainingTime, isReady } = getHatchProgress();
          return (
            <div className="leading-4 my-1 w-full text-xs text-pk-blue font-semibold  flex flex-col items-center gap-1">
              {isReady ? (
                <span className="text-center text-yellow-400 font-bold animate-pulse text-[9px] drop-shadow-lg">
                  ✨ READY TO HATCH!
                </span>
              ) : (
                <div className="w-full  absolute bottom-7 left-0 overflow-visible ">
                  <div className="w-full px-1 group-hover:hidden ">
                    <SegmentedProgressBar
                      ratio={ratio}
                      variant="orange"
                      maxSquares={8}
                      className="scale-60"
                    />
                  </div>
                  <span className="text-center hidden group-hover:block text-[9px]">
                    Left: {remainingTime}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

      <div className="text-lg leading-tight w-full px-1 mb-1 mt-auto">
        <div className="bg-pk-purple-3 pt-1 w-full h-full flex items-center justify-center">
          <StrokedText fontSize={12} strokeWidth={6} letterSpacing={1}>
            {`#${id.slice(0, 6)}`}
          </StrokedText>
        </div>
      </div>
    </div>
  );
};
